// src/shared/dashboard/shell/MobileNav.tsx
//
// Phone nav: a floating light paper bar in the warm claude.com grammar — cream
// surface, hairline border, the active section marked by a clay dot and
// ink-darkened icon + label. Includes a spacer so page content clears the
// fixed bar.
import type { NavSection } from "@/shared/dashboard/shell/nav";
import { Icon } from "@/shared/dashboard/shell/Icon";
import { navLabel } from "@/shared/dashboard/shell/navLabels";
import { go } from "@/shared/dashboard/shell/navigate";

export function MobileNav({
  sections,
  activeId,
  locale,
}: {
  sections: NavSection[];
  activeId: string;
  locale: string;
}) {
  return (
    <>
      <nav
        aria-label="Primary navigation"
        className="fixed bottom-3 left-3 right-3 z-50 flex items-stretch justify-around rounded-[14px] border border-[var(--line)] bg-[var(--paper-raised)]/95 px-1 shadow-[0_18px_50px_-18px_rgba(20,20,19,0.4)] backdrop-blur-xl lg:hidden"
        style={{ paddingBottom: "max(6px, env(safe-area-inset-bottom))" }}
      >
        {sections.slice(0, 5).map((s) => {
          const isActive = s.id === activeId;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => go(s.path)}
              aria-current={isActive ? "page" : undefined}
              className="relative flex min-h-[58px] flex-1 cursor-pointer flex-col items-center justify-center gap-1 pb-1 pt-2.5"
            >
              {isActive && (
                <span
                  aria-hidden
                  className="absolute top-1.5 h-1 w-1 rounded-full bg-[var(--accent)]"
                />
              )}
              <span
                className={
                  isActive
                    ? "text-[var(--ink-primary)]"
                    : "text-[var(--ink-muted)]"
                }
              >
                <Icon name={s.icon} size={20} />
              </span>
              <span
                className={
                  "text-[9px] font-medium leading-tight " +
                  (isActive
                    ? "text-[var(--ink-primary)]"
                    : "text-[var(--ink-muted)]")
                }
              >
                {navLabel(s.id, s.label, locale)}
              </span>
            </button>
          );
        })}
      </nav>
      <div
        className="h-[calc(5rem+env(safe-area-inset-bottom))] lg:hidden"
        aria-hidden
      />
    </>
  );
}
